// Phase 2 holds the underwriting answers: health conditions, diagnoses and medication, substance
// use, criminal history and bankruptcy. These are legitimate operational data — the quote cannot
// be priced without them — but they have no place in a behavioural analytics stream.
//
// HIPAA does not reach them: a life insurer is not a covered entity and life insurance is carved
// out of HIPAA's definition of a health plan. That gap is what the newer regimes were written to
// close. Washington's My Health My Data Act binds entities HIPAA does not, defines consumer
// health data to cover conditions, diagnoses and medication use whether self-reported or
// inferred, and carries a private right of action. The FTC's GoodRx and BetterHelp actions
// turned on this exact pattern: self-reported, non-HIPAA health answers reaching third-party
// analytics.
//
// The allowlist below is deliberately the inverse of a denylist. A field added to phase2 later is
// dropped by default, where a denylist would silently start forwarding it — the same reasoning
// that shapes LoggingApi.logAnonymous. Everything here must be safe to hand a third party.
//
// referralSources is "how did you hear about us": marketing attribution, not underwriting, and
// the one phase2 field analytics legitimately depends on.
const ANALYTICS_SAFE_PHASE2_FIELDS = ['referralSources'] as const;

/**
 * Reduces phase2 to the fields that may reach analytics, dropping the underwriting answers.
 *
 * Applied unconditionally rather than behind a consent check: these fields should not reach a
 * third-party analytics tool even for a visitor who accepted every category. Consent to
 * analytics cookies is not authorisation to disclose health data.
 *
 * The operational path is untouched — VULApi and the application submission still carry the
 * complete answers, which is what prices the policy.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const stripSensitiveAnswers = <T extends Record<string, any>>(payload: T): T => {
    const phase2 = payload?.data?.phase2;
    if (!phase2) return payload;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const safePhase2: Record<string, any> = {};
    ANALYTICS_SAFE_PHASE2_FIELDS.forEach((field) => {
        if (field in phase2) safePhase2[field] = phase2[field];
    });

    return {...payload, data: {...payload.data, phase2: safePhase2}};
};
